import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Building2, MapPin, Globe, ExternalLink, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  strategique: 'Sponsor stratégique', territorial: 'Sponsor territorial',
  filiere: 'Sponsor de filière', projet: 'Sponsor de projet',
  competences: 'Sponsor en compétences', nature: 'Sponsor en nature',
  media: 'Sponsor média', institutionnel: 'Partenaire institutionnel',
};

export default function FicheSponsorPage() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [status, setStatus] = useState('loading');
  const [resolvedSlug, setResolvedSlug] = useState(null);

  useEffect(() => {
    let current = true;
    setS(null);
    setStatus('loading');
    setResolvedSlug(null);
    axios.get(`${API}/api/sponsors/${slug}`)
      .then((r) => {
        if (!r.data || typeof r.data !== 'object' || !r.data.name) throw new Error('Invalid sponsor response');
        if (current) { setS(r.data); setStatus('ok'); setResolvedSlug(slug); }
      })
      .catch((error) => {
        if (current) {
          setStatus(error.response?.status === 404 ? 'notfound' : 'error');
          setResolvedSlug(slug);
        }
      });
    return () => { current = false; };
  }, [slug]);

  // The route-level metadata runs on navigation. Refine robots only after this
  // slug's public API response, so missing profiles cannot remain indexable.
  useEffect(() => {
    if (resolvedSlug !== slug || status === 'loading') return;
    const robots = document.head.querySelector('meta[name="robots"]') || document.createElement('meta');
    robots.name = 'robots';
    robots.content = status === 'ok' ? 'index, follow, max-image-preview:large' : 'noindex, follow';
    robots.dataset.oscopSeo = 'true';
    if (!robots.parentNode) document.head.appendChild(robots);
  }, [resolvedSlug, slug, status]);

  const logoSrc = s && s.logo_url ? (s.logo_url.startsWith('http') ? s.logo_url : `${API}${s.logo_url}`) : null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="fiche-sponsor-page">
      <Navigation />
      <div className="max-w-5xl mx-auto px-4 pt-24 pb-16">
        <BackButton />
        {status === 'loading' && <p className="mt-8 text-slate-500">Chargement…</p>}
        {(status === 'notfound' || status === 'error') && (
          <Card className="mt-8 bg-slate-900 border-slate-800"><CardContent className="py-16 text-center">
            <h1 className="text-2xl font-bold text-white mb-3">{status === 'notfound' ? 'Sponsor introuvable' : 'Fiche momentanément indisponible'}</h1>
            <p className="text-slate-300">{status === 'notfound' ? 'Cette fiche n’est pas publiée dans l’annuaire.' : 'La fiche n’a pas pu être chargée. Vous pouvez revenir à l’annuaire ou réessayer plus tard.'}</p>
            <Link to="/partenaires-sponsors"><Button className="mt-5 bg-emerald-600 hover:bg-emerald-500 rounded-full">Retour à l'annuaire</Button></Link>
          </CardContent></Card>
        )}
        {status === 'ok' && s && (
          <div className="mt-6">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <div className="w-28 h-28 rounded-2xl bg-white/5 border border-slate-800 flex items-center justify-center p-3 flex-shrink-0">
                {logoSrc ? <img src={logoSrc} alt={s.name} className="max-h-full max-w-full object-contain" /> : <Building2 className="w-12 h-12 text-slate-600" />}
              </div>
              <div className="flex-1">
                <h1 className="text-3xl sm:text-4xl font-bold text-white" data-testid="sponsor-name">{s.name}</h1>
                <div className="mt-3 flex flex-wrap gap-2">
                  {s.sponsor_category && <Badge className="bg-emerald-500/15 text-emerald-300 border-0 font-semibold">{CATEGORY_LABELS[s.sponsor_category] || s.sponsor_category}</Badge>}
                  {(s.territories || []).map((t) => <Badge key={t} className="bg-slate-800 text-white text-slate-300 border-0 font-semibold"><MapPin className="w-3 h-3 mr-1" />{t}</Badge>)}
                </div>
                {s.website_url && (
                  <a href={s.website_url} target="_blank" rel="sponsored noopener noreferrer"
                    aria-label={`Consulter le site du sponsor ${s.name}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300">
                    <Globe className="w-4 h-4" /> Site internet <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>

            {(s.full_description || s.short_description) && (
              <Card className="mt-8 bg-slate-900 border-slate-800"><CardContent className="p-6">
                <h2 className="text-lg font-semibold text-white mb-2">Présentation</h2>
                <p className="text-slate-400 whitespace-pre-line">{s.full_description || s.short_description}</p>
                {s.secteur_activite && <p className="mt-3 text-sm text-slate-500">Secteur : {s.secteur_activite}</p>}
              </CardContent></Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
              <Card className="bg-slate-900 border-slate-800"><CardContent className="p-6">
                <h2 className="text-lg font-semibold text-white mb-3">Engagement auprès d'O'SCOP</h2>
                <dl className="space-y-2 text-sm">
                  {(s.branches || []).length > 0 && <div><dt className="text-slate-500">Branches soutenues</dt><dd className="text-slate-200">{s.branches.join(', ')}</dd></div>}
                  {(s.contribution_types || []).length > 0 && <div><dt className="text-slate-500">Type de contribution</dt><dd className="text-slate-200">{s.contribution_types.join(', ')}</dd></div>}
                  {(s.supported_projects || []).length > 0 && <div><dt className="text-slate-500">Projets accompagnés</dt><dd className="text-slate-200">{s.supported_projects.join(', ')}</dd></div>}
                  {s.start_date && <div><dt className="text-slate-500">Partenaire depuis</dt><dd className="text-slate-200">{String(s.start_date).slice(0, 4)}</dd></div>}
                  {s.amount_is_public && s.public_amount != null && <div><dt className="text-slate-500">Contribution publiée</dt><dd className="text-slate-200">{Number(s.public_amount).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</dd></div>}
                </dl>
              </CardContent></Card>

              {(s.impact_indicators || []).length > 0 && (
                <Card className="bg-slate-900 border-slate-800"><CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-white mb-3">Impact constaté</h2>
                  <ul className="space-y-1.5 text-sm text-slate-300">
                    {s.impact_indicators.map((ind, i) => <li key={i}>• {ind.label || ind.nom} : <strong>{ind.value ?? ind.valeur}</strong></li>)}
                  </ul>
                </CardContent></Card>
              )}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/devenir-sponsor"><Button className="bg-emerald-600 hover:bg-emerald-500 rounded-full">Devenir sponsor à votre tour <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
              <Link to="/partenaires-sponsors"><Button variant="outline" className="rounded-full border-slate-700 text-slate-200 hover:bg-slate-800">Voir tous les partenaires</Button></Link>
            </div>

            <p className="mt-8 text-xs text-slate-600 border-t border-slate-800 pt-6">
              Le statut de sponsor ou de partenaire ne confère aucun droit automatique de gouvernance, aucun droit de
              préférence commerciale et aucune priorité dans l'attribution des marchés ou contrats d'OBJECTIF SCOP OUTREMER.
              Toute admission comme associé de la SCIC fait l'objet d'une procédure distincte conformément aux statuts.
            </p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
